import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Pane, PaneFooter, Button, Loading, Icon } from '@folio/stripes-components';
import useCatalogingAgent, { JOB_STATUS } from '../hooks/useCatalogingAgent';
import JobProgressTracker from './JobProgressTracker';
import FieldProposalCard from './FieldProposalCard';

const CatalogingAgentPanel = ({
  onClose,
  onApplyChanges,
  templateId,
  instanceId,
}) => {
  const [selectedFile, setSelectedFile]   = useState(null);
  const [filePreview, setFilePreview]     = useState(null);
  const [decisions, setDecisions]         = useState({});

  const {
    status, job, proposals, changeSet, events, error,
    isLoading, isIdle, isRunning, isAwaitingReview, isReadyToApply,
    startJob, submitDecisions, applyChanges, cancel, reset,
  } = useCatalogingAgent({ templateId, instanceId, onApplyChanges });

  // ── File selection ────────────────────────────────────────────────────────
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFilePreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  // ── Start ─────────────────────────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    if (!selectedFile) return;
    setDecisions({});
    await startJob(selectedFile);
  }, [selectedFile, startJob]);

  // ── Review decisions ──────────────────────────────────────────────────────
  const handleDecision = useCallback(({ proposalId, decision, modifiedValue }) => {
    setDecisions(prev => {
      if (!decision) {
        const next = { ...prev };
        delete next[proposalId];
        return next;
      }
      return { ...prev, [proposalId]: { proposalId, decision, modifiedValue } };
    });
  }, []);

  const handleSubmitReview = useCallback(async () => {
    const decisionList = Object.values(decisions);
    if (!decisionList.length) return;
    await submitDecisions(decisionList);
  }, [decisions, submitDecisions]);

  const handleApply = useCallback(async () => {
    await applyChanges();
    onClose();
  }, [applyChanges, onClose]);

  // ── Footer ────────────────────────────────────────────────────────────────
  const renderFooter = () => {
    if (isIdle) {
      return (
        <PaneFooter
          renderEnd={
            <Button buttonStyle="primary" onClick={handleStart} disabled={!selectedFile || isLoading} marginBottom0>
              {isLoading
                ? <><Icon icon="spinner-ellipsis" /> <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.starting" defaultMessage="Starting..." /></>
                : <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.start" defaultMessage="Start Cataloging" />
              }
            </Button>
          }
        />
      );
    }

    if (isAwaitingReview) {
      const decidedCount = Object.keys(decisions).length;
      return (
        <PaneFooter
          renderEnd={
            <Button
              buttonStyle="primary"
              onClick={handleSubmitReview}
              disabled={decidedCount === 0 || isLoading}
              marginBottom0
            >
              {isLoading
                ? <Loading size="small" />
                : <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.submitReview" defaultMessage="Submit Review ({count}/{total})" values={{ count: decidedCount, total: proposals.length }} />
              }
            </Button>
          }
          renderStart={
            <Button buttonStyle="default" onClick={cancel} marginBottom0>
              <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.cancel" defaultMessage="Cancel" />
            </Button>
          }
        />
      );
    }

    if (isReadyToApply && changeSet) {
      return (
        <PaneFooter
          renderEnd={
            <Button buttonStyle="primary" onClick={handleApply} disabled={isLoading} marginBottom0>
              {isLoading
                ? <Loading size="small" />
                : <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.applyToForm" defaultMessage="Apply to Form ({count} changes)" values={{ count: changeSet.itemCount }} />
              }
            </Button>
          }
          renderStart={
            <Button buttonStyle="default" onClick={reset} marginBottom0>
              <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.startOver" defaultMessage="Start Over" />
            </Button>
          }
        />
      );
    }

    if (isRunning) {
      return (
        <PaneFooter
          renderStart={
            <Button buttonStyle="default" onClick={cancel} marginBottom0>
              <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.cancel" defaultMessage="Cancel" />
            </Button>
          }
        />
      );
    }

    return null;
  };

  return (
    <Pane
      id="cataloging-agent-panel"
      defaultWidth="40%"
      paneTitle={<FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.title" defaultMessage="AI Cataloging Assistant" />}
      dismissible
      onClose={onClose}
      footer={renderFooter()}
    >
      {/* File upload — only when idle */}
      {isIdle && (
        <div style={{ padding: '16px 0' }}>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.uploadHint" defaultMessage="Upload a cover image or title page to extract bibliographic data automatically." />
          </p>

          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileSelect}
            style={{ marginBottom: '12px' }}
          />

          {filePreview && (
            <img
              src={filePreview}
              alt="preview"
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px', border: '1px solid #ddd' }}
            />
          )}
        </div>
      )}

      {/* Progress tracker */}
      {!isIdle && (
        <JobProgressTracker status={status} events={events} error={error} />
      )}

      {/* Proposals review */}
      {isAwaitingReview && proposals.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <h4 style={{ marginBottom: '12px', fontSize: '14px' }}>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.reviewProposals" defaultMessage="Review {count} field proposals" values={{ count: proposals.length }} />
          </h4>
          {proposals.map(proposal => (
            <FieldProposalCard
              key={proposal.id}
              proposal={proposal}
              onDecision={handleDecision}
            />
          ))}
        </div>
      )}

      {/* Change set summary */}
      {isReadyToApply && changeSet && (
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#E8F5E9', borderRadius: '6px' }}>
          <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#2E7D32' }}>
            <Icon icon="check-circle" />
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.readyToApply" defaultMessage=" {count} changes approved and ready to apply" values={{ count: changeSet.itemCount }} />
          </h4>
          {changeSet.items?.map((item, i) => (
            <div key={i} style={{ fontSize: '12px', color: '#555', padding: '2px 0' }}>
              ✓ {item.fieldPath}
            </div>
          ))}
        </div>
      )}

      {/* Applied confirmation */}
      {status === JOB_STATUS.APPLIED_TO_FORM && (
        <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#E8F5E9', borderRadius: '6px', textAlign: 'center' }}>
          <Icon icon="check-circle" style={{ fontSize: '32px', color: '#4CAF50' }} />
          <p style={{ marginTop: '8px', fontWeight: 600 }}>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.applied" defaultMessage="Changes applied to form successfully!" />
          </p>
          <Button buttonStyle="default" onClick={reset} marginBottom0 style={{ marginTop: '8px' }}>
            <FormattedMessage id="ui-plugin-inventory-templates.catalogingAgent.startNew" defaultMessage="Start New Job" />
          </Button>
        </div>
      )}
    </Pane>
  );
};

CatalogingAgentPanel.propTypes = {
  onClose:        PropTypes.func.isRequired,
  onApplyChanges: PropTypes.func.isRequired,
  templateId:     PropTypes.string,
  instanceId:     PropTypes.string,
};

export default CatalogingAgentPanel;
